// --- NEW FILE: assets/quiz-animations.js ---
// --- NEW: animation only, no functional change ---
//
// This file is 100% additive. It never imports, calls, or modifies anything
// in avpc-product-quiz.js. It only:
//   1. Observes DOM mutations that the existing component already makes
//      (adding/removing the `avpc-pq-active` class, toggling `hidden`, etc.)
//   2. Adds/removes purely presentational CSS classes and CSS custom
//      properties (e.g. --progress) in response.
//   3. Adds its own event listeners for hover/tilt/ripple/scroll-reveal —
//      all additive `data-animate-*` targeting, never colliding with the
//      functional `data-*` attributes (data-action, data-email-form, etc.)
//
// Load this AFTER avpc-product-quiz.js (defer + defer both fine, this file
// only touches DOM/classes and doesn't need the component's JS API at all).

(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const IS_TOUCH = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  // ---------------------------------------------------------------------
  // Boot: one instance of AVPCQuizAnimator per <avpc-product-quiz> element,
  // scoped strictly to that section instance via [data-section-id].
  // ---------------------------------------------------------------------
  function initAll() {
    document.querySelectorAll('avpc-product-quiz[data-section-id]').forEach((root) => {
      if (root._avpcAnimatorAttached) return; // never double-init
      root._avpcAnimatorAttached = true;
      new AVPCQuizAnimator(root);
    });
  }

  class AVPCQuizAnimator {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.container = root.querySelector('.avpc-pq-quiz-container');
      this.slides = Array.from(root.querySelectorAll('.avpc-pq-quiz-slide'));
      this.questionSlides = this.slides.filter((s) => s.dataset.slide === 'question');
      this.prevActiveIndex = this.getActiveIndex();
      this.isEditorMode = false;

      this.ensureProgressBar();
      this.ensureResultExtras();
      this.bindSlideObserver();
      this.bindOptionInteractions();
      this.bindScrollReveal();
      this.bindShopifyEditorEvents();
      this.updateProgressBar(); // set initial state
    }

    // -- helpers ----------------------------------------------------------

    getActiveIndex() {
      return this.slides.findIndex((s) => s.classList.contains('avpc-pq-active'));
    }

    // -- 1. Progress bar (visual), driven by --progress ------------------
    //
    // The existing markup only has a text label ([data-progress]); we add a
    // slim visual bar ABOVE the quiz container. New elements only, no
    // collision with functional selectors.
    ensureProgressBar() {
      if (!this.container || this.questionSlides.length === 0) return;
      if (this.root.querySelector('[data-animate-progress-track]')) return;

      const track = document.createElement('div');
      track.setAttribute('data-animate-progress-track', '');
      track.className = 'avpc-anim-progress-track';
      const fill = document.createElement('div');
      fill.setAttribute('data-animate-progress-fill', '');
      fill.className = 'avpc-anim-progress-fill';
      track.appendChild(fill);

      this.container.insertAdjacentElement('beforebegin', track);
      this.progressFill = fill;
    }

    updateProgressBar() {
      if (!this.progressFill || !this.questionSlides.length) return;
      const activeSlide = this.slides[this.getActiveIndex()];
      let ratio = 0;

      if (activeSlide) {
        const qIndex = this.questionSlides.indexOf(activeSlide);
        if (qIndex >= 0) {
          ratio = (qIndex + 1) / this.questionSlides.length;
        } else if (
          activeSlide.dataset.slide === 'email-capture' ||
          activeSlide.dataset.slide === 'results' ||
          activeSlide.dataset.slide === 'results-placeholder'
        ) {
          ratio = 1;
        }
      }

      this.root.style.setProperty('--progress', ratio.toFixed(4));
    }

    // -- 2. Direction-aware transitions + result reveal + loader ---------
    //
    // We watch `class` changes on every slide. The functional code toggles
    // `avpc-pq-active` — we react to that, we never set it ourselves.
    bindSlideObserver() {
      const observer = new MutationObserver((mutations) => {
        let activeChanged = false;
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            activeChanged = true;
          }
        }
        if (activeChanged) this.handleActiveSlideChanged();
      });

      this.slides.forEach((slide) => {
        observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
      });
      this._slideObserver = observer;
    }

    handleActiveSlideChanged() {
      const newIndex = this.getActiveIndex();
      if (newIndex === -1 || newIndex === this.prevActiveIndex) return;

      const direction = newIndex > this.prevActiveIndex ? 'forward' : 'back';
      const newSlide = this.slides[newIndex];

      // direction-aware entrance class, auto-removed after animation
      if (!REDUCED_MOTION && newSlide) {
        const cls = direction === 'forward' ? 'avpc-anim-enter-fwd' : 'avpc-anim-enter-back';
        newSlide.classList.add(cls);
        newSlide.addEventListener(
          'animationend',
          () => newSlide.classList.remove(cls),
          { once: true }
        );
      }

      this.prevActiveIndex = newIndex;
      this.updateProgressBar();

      // "Calculating your results" loader: shown briefly right as we land
      // on a results / results-placeholder slide (i.e. right after email
      // submit resolves and the functional code calls goToResultSlide()).
      if (newSlide && (newSlide.dataset.slide === 'results' || newSlide.dataset.slide === 'results-placeholder')) {
        this.playResultReveal(newSlide);
      }
    }

    // -- 3. Calculating loader + count-up + confetti on result reveal ----
    ensureResultExtras() {
      // Confetti canvas + loader overlay, one each, appended once to root.
      if (!this.root.querySelector('[data-animate-loader]')) {
        const loader = document.createElement('div');
        loader.setAttribute('data-animate-loader', '');
        loader.className = 'avpc-anim-loader';
        loader.hidden = true;
        loader.innerHTML =
          '<div class="avpc-anim-loader-spinner"></div><div class="avpc-anim-loader-text">Calculating your results…</div>';
        this.root.appendChild(loader);
        this.loaderEl = loader;
      }

      if (!this.root.querySelector('[data-animate-confetti]')) {
        const canvas = document.createElement('canvas');
        canvas.setAttribute('data-animate-confetti', '');
        canvas.className = 'avpc-anim-confetti';
        this.root.appendChild(canvas);
        this.confettiCanvas = canvas;
      }
    }

    playResultReveal(resultSlide) {
      if (REDUCED_MOTION) return;

      // Brief "calculating" loader overlay (visual only — it does not gate
      // or delay any functional logic, which has already completed by the
      // time this slide became active).
      if (this.loaderEl) {
        this.loaderEl.hidden = false;
        this.loaderEl.classList.add('avpc-anim-loader-visible');
        setTimeout(() => {
          this.loaderEl.classList.remove('avpc-anim-loader-visible');
          setTimeout(() => {
            this.loaderEl.hidden = true;
          }, 250);
        }, 650);
      }

      if (resultSlide.dataset.slide === 'results') {
        this.burstConfetti();
      }
    }

    burstConfetti() {
      const canvas = this.confettiCanvas;
      if (!canvas) return;
      const rect = this.root.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.opacity = '1';

      const ctx = canvas.getContext('2d');
      const colors = ['#ff6b6b', '#feca57', '#48dbfb', '#1dd1a1', '#a29bfe'];
      const particles = Array.from({ length: 70 }, () => ({
        x: rect.width / 2,
        y: rect.height * 0.25,
        vx: (Math.random() - 0.5) * 9,
        vy: Math.random() * -7 - 3,
        size: Math.random() * 6 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
      }));

      let frame = 0;
      const maxFrames = 90;

      function tick() {
        frame++;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particles.forEach((p) => {
          p.vy += 0.18; // gravity
          p.x += p.vx;
          p.y += p.vy;
          p.rotation += p.vr;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
          ctx.restore();
        });
        if (frame < maxFrames) {
          requestAnimationFrame(tick);
        } else {
          canvas.style.opacity = '0';
          setTimeout(() => ctx.clearRect(0, 0, canvas.width, canvas.height), 400);
        }
      }
      requestAnimationFrame(tick);
    }

    // -- 4. Option card tilt / ripple micro-interactions ------------------
    bindOptionInteractions() {
      if (REDUCED_MOTION) return;

      this.root.querySelectorAll('.avpc-pq-answer-option').forEach((option) => {
        if (option._avpcInteractionsBound) return;
        option._avpcInteractionsBound = true;

        if (!IS_TOUCH) {
          option.addEventListener('mousemove', (e) => {
            const rect = option.getBoundingClientRect();
            const px = (e.clientX - rect.left) / rect.width - 0.5;
            const py = (e.clientY - rect.top) / rect.height - 0.5;
            option.style.setProperty('--tilt-x', (py * -4).toFixed(2) + 'deg');
            option.style.setProperty('--tilt-y', (px * 4).toFixed(2) + 'deg');
          });
          option.addEventListener('mouseleave', () => {
            option.style.setProperty('--tilt-x', '0deg');
            option.style.setProperty('--tilt-y', '0deg');
          });
        }

        option.addEventListener('click', (e) => {
          const rect = option.getBoundingClientRect();
          const ripple = document.createElement('span');
          ripple.className = 'avpc-anim-ripple';
          ripple.style.left = e.clientX - rect.left + 'px';
          ripple.style.top = e.clientY - rect.top + 'px';
          option.appendChild(ripple);
          ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
        });
      });
    }

    // -- 5. Scroll-reveal for the whole section ---------------------------
    bindScrollReveal() {
      if (!('IntersectionObserver' in window)) return;
      this.root.classList.add('avpc-anim-scroll-pending');

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.root.classList.remove('avpc-anim-scroll-pending');
              this.root.classList.add('avpc-anim-scroll-revealed');
              io.disconnect();
            }
          });
        },
        { threshold: 0.15 }
      );
      io.observe(this.root);
    }

    // -- 6. Theme editor safety: block select/reorder preview -------------
    bindShopifyEditorEvents() {
      document.addEventListener('shopify:block:select', (event) => {
        const block = event.target.closest(`[data-section-id="${this.sectionId}"] [data-block-id]`);
        if (!block) return;
        this.isEditorMode = true;
        // Pause our own decorative animations while the editor is
        // previewing a specific block, so nothing double-fires.
        this.root.classList.add('avpc-anim-editor-paused');
      });

      document.addEventListener('shopify:block:deselect', () => {
        this.isEditorMode = false;
        this.root.classList.remove('avpc-anim-editor-paused');
      });

      // Re-scan for newly rendered option elements after any Shopify
      // section reload in the editor (merchant edits settings live).
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.querySelector && event.target.querySelector(`[data-section-id="${this.sectionId}"]`)) {
          this.bindOptionInteractions();
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Custom elements can connect after DOMContentLoaded (e.g. lazy sections);
  // catch those too without polling.
  const bodyObserver = new MutationObserver(() => initAll());
  bodyObserver.observe(document.body, { childList: true, subtree: true });
})();
